import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  GitBranch, Send, CheckCircle2, Save, X, Search,
  ChevronLeft, ChevronRight,
} from "lucide-react";
import ImportButton from "@/components/ImportButton";
import SalesVersionsDrawer, { SalesSubmitModal } from "@/components/SalesVersionsDrawer";
import { PageShell } from "@/components/SidebarLayout";
import HorizonSelector from "@/components/HorizonSelector";
import { api, formatApiError } from "@/lib/api";
import { formatMoney } from "@/lib/format";
import { useIsMobile } from "@/lib/useMediaQuery";
import { useProjectFilter } from "@/lib/ProjectFilterContext";
import { TEST_IDS } from "@/testIds";

function shortMonthLabel(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-IN", { month: "short", year: "2-digit" });
  } catch { return iso; }
}

function typeColor(t) {
  return t === "new" ? "bg-gold-500" : t === "ongoing" ? "bg-ink" : "bg-muted-light";
}

// Cell key + value helpers
const cellKey = (pid, m) => `${pid}::${m}`;
function parseNum(s) {
  if (s === "" || s == null) return 0;
  const n = parseFloat(String(s).replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
}

// ---------------- Desktop Grid ---------------- //
function GridDesktop({ data, edits, setEdit }) {
  const months = data?.months || [];
  const rows = data?.rows || [];
  if (!rows.length) {
    return (
      <div className="cf-card p-8 text-center text-muted-light text-sm">
        No projects to display.
      </div>
    );
  }

  // Compute live totals overlaying edits
  const liveCell = (r, m) => {
    const key = cellKey(r.project_id, m);
    return key in edits ? parseNum(edits[key]) : (r.cells[m] || 0);
  };
  const liveRowTotal = (r) => months.reduce((s, m) => s + liveCell(r, m), 0);
  const liveColTotal = (m) => rows.reduce((s, r) => s + liveCell(r, m), 0);
  const liveGrand = rows.reduce((s, r) => s + liveRowTotal(r), 0);

  return (
    <div className="cf-card overflow-hidden">
      <div className="overflow-auto" style={{ maxHeight: "72vh" }}>
        <table className="w-full border-collapse">
          <thead className="sticky top-0 z-20">
            <tr>
              <th
                className="sticky left-0 z-30 bg-ink text-white text-[10px] uppercase tracking-widest font-semibold px-4 py-3 text-left border-b border-hairline"
                style={{ minWidth: 220 }}
              >
                <div>Project</div>
                <div className="text-[9px] text-white/50 font-normal normal-case tracking-normal mt-0.5">
                  Monthly sales bookings (₹L)
                </div>
              </th>
              {months.map((m) => (
                <th
                  key={m}
                  className="bg-ink text-white text-[10px] uppercase tracking-widest font-semibold px-2 py-3 text-right border-b border-hairline"
                  style={{ minWidth: 96 }}
                >
                  {shortMonthLabel(m)}
                </th>
              ))}
              <th
                className="sticky right-0 z-30 bg-ink text-white text-[10px] uppercase tracking-widest font-semibold px-3 py-3 text-right border-b border-hairline"
                style={{ minWidth: 150, borderLeft: "1px solid rgba(255,255,255,0.2)" }}
              >
                Row Total
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const rowTotal = liveRowTotal(r);
              return (
                <tr
                  key={r.project_id}
                  className={`cf-row-in border-b border-hairline/40 ${i % 2 === 0 ? "bg-surface" : "bg-paper/40"}`}
                  style={{ animationDelay: `${Math.min(i, 15) * 20}ms` }}
                >
                  <td
                    className="sticky left-0 z-10 px-4 py-2 border-r border-hairline/50"
                    style={{ minWidth: 220, background: i % 2 === 0 ? "var(--cf-surface)" : "var(--cf-surface-2)" }}
                  >
                    <div className="flex items-center gap-2">
                      <span className={`inline-block w-1.5 h-1.5 rounded-full ${typeColor(r.project_type)}`} />
                      <span className="text-sm text-ink font-semibold truncate">{r.project_name}</span>
                      <span className="text-[9px] uppercase tracking-widest text-muted-light">{r.project_type}</span>
                    </div>
                  </td>
                  {months.map((m) => {
                    const key = cellKey(r.project_id, m);
                    const isDirty = key in edits;
                    const raw = isDirty ? edits[key] : (r.cells[m] || 0);
                    const display = raw === 0 || raw === "0" || raw === "" ? "" : String(raw);
                    return (
                      <td
                        key={m}
                        data-testid={TEST_IDS.salesGridCell(r.project_id, m)}
                        className={`px-1 py-1 border-l border-hairline/40 align-middle ${isDirty ? "bg-gold-500/10" : ""}`}
                        style={{ minWidth: 96 }}
                      >
                        <input
                          data-testid={TEST_IDS.salesGridCellInput(r.project_id, m)}
                          type="text"
                          inputMode="decimal"
                          value={display}
                          onChange={(e) => setEdit(key, e.target.value)}
                          placeholder="0"
                          className="num w-full text-right text-sm px-2 py-1.5 rounded bg-transparent focus:outline-none focus:bg-surface focus:ring-2 focus:ring-gold-500/40 text-ink placeholder:text-muted-light/40"
                        />
                      </td>
                    );
                  })}
                  <td
                    className="sticky right-0 z-10 px-3 py-2 text-right border-l border-hairline align-middle"
                    style={{ minWidth: 150, background: i % 2 === 0 ? "var(--cf-surface)" : "var(--cf-surface-2)" }}
                  >
                    <div className="num text-sm text-ink font-bold">{formatMoney(rowTotal, { showZero: true })}</div>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot className="sticky bottom-0 z-20">
            <tr>
              <th
                className="sticky left-0 z-30 bg-ink text-white text-[10px] uppercase tracking-widest font-semibold px-4 py-3 text-left border-t border-hairline"
                style={{ minWidth: 220 }}
              >
                Column Totals
              </th>
              {months.map((m) => (
                <td
                  key={m}
                  className="bg-ink text-white text-[11px] text-right px-2 py-2 num border-l border-white/10 align-middle"
                  style={{ minWidth: 96 }}
                >
                  <span className="font-bold">{formatMoney(liveColTotal(m), { showZero: true })}</span>
                </td>
              ))}
              <td
                className="sticky right-0 z-30 bg-ink text-white px-3 py-2 text-right num align-middle"
                style={{ minWidth: 150, borderLeft: "1px solid rgba(255,255,255,0.2)" }}
              >
                <div className="text-[9px] uppercase tracking-widest opacity-70">Grand Total</div>
                <div className="text-base font-bold">{formatMoney(liveGrand, { showZero: true })}</div>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

// ---------------- Mobile Grid: project-focus, one card at a time ---------------- //
function GridMobile({ data, edits, setEdit }) {
  const rows = data?.rows || [];
  const months = data?.months || [];
  const [idx, setIdx] = useState(0);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (idx >= rows.length) setIdx(0);
  }, [rows.length, idx]);

  const filtered = search.trim()
    ? rows.filter((r) => r.project_name.toLowerCase().includes(search.toLowerCase()))
    : rows;
  const clamped = Math.min(idx, Math.max(0, filtered.length - 1));
  const current = filtered[clamped];

  if (!current) {
    return (
      <div className="cf-card p-8 text-center text-muted-light text-sm">
        No projects match &ldquo;{search}&rdquo;.
      </div>
    );
  }

  const liveCell = (m) => {
    const key = cellKey(current.project_id, m);
    return key in edits ? parseNum(edits[key]) : (current.cells[m] || 0);
  };
  const rowTotal = months.reduce((s, m) => s + liveCell(m), 0);

  return (
    <div className="space-y-3 pb-24">
      <div className="cf-card px-3 py-2.5 flex items-center gap-2">
        <Search size={14} className="text-muted-light shrink-0" />
        <input
          type="text"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setIdx(0); }}
          placeholder="Find project…"
          className="flex-1 bg-transparent focus:outline-none text-sm text-ink placeholder:text-muted-light"
        />
        {filtered.length > 1 && (
          <>
            <button
              onClick={() => setIdx((i) => Math.max(0, i - 1))}
              disabled={clamped === 0}
              className="w-8 h-8 rounded-full bg-paper flex items-center justify-center disabled:opacity-30"
              aria-label="Previous project"
            >
              <ChevronLeft size={14} />
            </button>
            <span className="text-[11px] text-muted-dark num whitespace-nowrap">
              {clamped + 1}/{filtered.length}
            </span>
            <button
              onClick={() => setIdx((i) => Math.min(filtered.length - 1, i + 1))}
              disabled={clamped === filtered.length - 1}
              className="w-8 h-8 rounded-full bg-paper flex items-center justify-center disabled:opacity-30"
              aria-label="Next project"
            >
              <ChevronRight size={14} />
            </button>
          </>
        )}
      </div>

      <div className="cf-card p-4">
        <div className="flex items-center gap-2 mb-1">
          <span className={`inline-block w-2 h-2 rounded-full ${typeColor(current.project_type)}`} />
          <span className="text-base text-ink font-semibold flex-1 truncate">{current.project_name}</span>
          <span className="text-[10px] uppercase tracking-widest text-muted-light">{current.project_type}</span>
        </div>
        <div className="text-[10px] uppercase tracking-widest text-muted-light">Row Total</div>
        <div className="num text-2xl text-ink font-bold">{formatMoney(rowTotal, { showZero: true })}</div>
      </div>

      <div className="space-y-2">
        {months.map((m) => {
          const key = cellKey(current.project_id, m);
          const isDirty = key in edits;
          const raw = isDirty ? edits[key] : (current.cells[m] || 0);
          const display = raw === 0 || raw === "0" || raw === "" ? "" : String(raw);
          return (
            <div
              key={m}
              data-testid={TEST_IDS.salesGridCell(current.project_id, m)}
              className={`cf-card p-3 flex items-center gap-3 ${isDirty ? "ring-2 ring-gold-500/40" : ""}`}
            >
              <div className="min-w-[70px]">
                <div className="text-[10px] uppercase tracking-widest text-muted-light">Month</div>
                <div className="text-sm text-ink font-semibold">{shortMonthLabel(m)}</div>
              </div>
              <input
                data-testid={TEST_IDS.salesGridCellInput(current.project_id, m)}
                type="text"
                inputMode="decimal"
                value={display}
                onChange={(e) => setEdit(key, e.target.value)}
                placeholder="0"
                className="num flex-1 text-right text-lg px-3 py-2 rounded-lg bg-paper focus:outline-none focus:ring-2 focus:ring-gold-500/40 text-ink placeholder:text-muted-light/40 min-h-[44px]"
              />
              <div className="text-[10px] uppercase tracking-widest text-muted-light">₹L</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------- Page ---------------- //
export default function SalesBookings() {
  const { project } = useProjectFilter();
  const isMobile = useIsMobile();
  const [horizon, setHorizon] = useState(24);
  const [grid, setGrid] = useState(null);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);
  const [activeVersion, setActiveVersion] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [submitOpen, setSubmitOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [g, v] = await Promise.all([
        api.get(`/sales-bookings/grid?horizon=${horizon}`),
        api.get("/sales-versions"),
      ]);
      setGrid(g.data);
      setActiveVersion(v.data.find((x) => x.is_active) || null);
    } catch (e) {
      toast.error("Load failed", { description: formatApiError(e.response?.data?.detail) });
    }
  }, [horizon]);

  useEffect(() => { load(); }, [load]);

  // Filter grid rows by global project filter
  const filteredGrid = useMemo(() => {
    if (!grid || project === "all") return grid;
    return { ...grid, rows: grid.rows.filter((r) => r.project_name === project) };
  }, [grid, project]);

  const setEdit = (key, value) => {
    setEdits((prev) => {
      const next = { ...prev };
      // Only track when different from current baseline; empty is treated as 0
      const [pid, m] = key.split("::");
      const row = grid?.rows.find((r) => r.project_id === pid);
      const baseline = row?.cells?.[m] || 0;
      const parsed = parseNum(value);
      if (parsed === baseline && value !== "") {
        delete next[key];
      } else {
        next[key] = value;
      }
      return next;
    });
  };

  const discard = () => setEdits({});

  const save = async () => {
    const rows = Object.entries(edits).map(([key, value]) => {
      const [project_id, month] = key.split("::");
      return { project_id, month, sales_value: parseNum(value) };
    });
    if (!rows.length) return;
    setSaving(true);
    try {
      const res = await api.post("/sales-bookings/bulk", { rows });
      const { upserted = 0, updated = 0, failed = 0 } = res.data || {};
      if (failed > 0) {
        toast.error(`Saved ${upserted + updated}, ${failed} failed`);
      } else {
        toast.success(`Saved ${upserted + updated} cells · Collections re-computed`);
      }
      setEdits({});
      await load();
    } catch (e) {
      toast.error("Save failed", { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setSaving(false);
    }
  };

  const dirtyCount = Object.keys(edits).length;

  return (
    <PageShell
      testId={TEST_IDS.salesGridPage}
      kicker="Sales Data · Excel-like Grid"
      title="Sales Bookings"
      actions={
        <div className="cf-hscroll -mx-4 md:mx-0 px-4 md:px-0 w-full md:w-auto">
          <div className="inline-flex items-center gap-2">
            <HorizonSelector value={horizon} onChange={setHorizon} />
            <ImportButton testId="sales-import" onDone={load} />
            <button
              data-testid={TEST_IDS.salesVersionsBtn}
              onClick={() => setDrawerOpen(true)}
              className="cf-touch inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-surface text-ink text-sm font-medium hover:bg-surface-2 transition-colors border border-hairline"
              title="View version history"
            >
              <GitBranch size={13} /> History
            </button>
            <button
              data-testid={TEST_IDS.salesSubmitBtn}
              onClick={() => setSubmitOpen(true)}
              className="cf-touch inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-ink text-white text-sm font-medium hover:bg-ink/90 transition-colors"
              title="Submit current data as new version"
            >
              <Send size={13} className="text-gold-500" /> Submit
            </button>
          </div>
        </div>
      }
    >
      <div className="flex flex-wrap items-center gap-3 -mt-2">
        <p className="text-muted-dark text-sm max-w-2xl">
          Rows = projects, columns = months. Edit any cell (₹ Lakhs) — changes are batched and applied together. Saving re-runs the projection so the <span className="font-semibold text-ink">Collections</span> tab updates instantly.
          <span className="ml-2 text-[10.5px] text-muted-light">
            · <span className="font-semibold text-ink">Land projects (Avigna) show/edit the Dev. Fee</span> — the collectable ~15% of plot value; the gross sale value auto-derives at fee ÷ 15%.
          </span>
          <span className="ml-2 text-[10.5px] text-muted-light">
            · Import Excel accepts a <span className="font-semibold text-ink">Project | Month | ₹L</span> sheet (any name) — Aug-26 stays put, later months feed the projection.
          </span>
        </p>
        {activeVersion && (
          <span
            data-testid={TEST_IDS.salesActiveVersionPill}
            className="cf-pill num"
            style={{ background: "rgba(251,192,45,0.15)", color: "#8A6800" }}
            title={activeVersion.note || ""}
          >
            <CheckCircle2 size={11} /> Active · v{activeVersion.version_no}
          </span>
        )}
      </div>

      {isMobile
        ? <GridMobile data={filteredGrid} edits={edits} setEdit={setEdit} />
        : <GridDesktop data={filteredGrid} edits={edits} setEdit={setEdit} />
      }

      {/* Floating save bar */}
      {dirtyCount > 0 && (
        <div className="fixed md:sticky bottom-4 left-4 right-4 md:left-auto md:right-6 md:w-auto md:ml-auto z-40 pointer-events-none">
          <div className="cf-card pointer-events-auto flex items-center gap-3 p-3 shadow-xl border border-gold-500/40 md:inline-flex md:justify-end">
            <div className="text-sm text-ink">
              <span className="font-semibold">{dirtyCount}</span>{" "}
              <span className="text-muted-dark">unsaved {dirtyCount === 1 ? "cell" : "cells"}</span>
            </div>
            <button
              data-testid={TEST_IDS.salesGridDiscardBtn}
              onClick={discard}
              disabled={saving}
              className="cf-touch inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-paper text-muted-dark text-sm font-medium hover:text-ink transition-colors"
            >
              <X size={13} /> Discard
            </button>
            <button
              data-testid={TEST_IDS.salesGridSaveBtn}
              onClick={save}
              disabled={saving}
              className="cf-touch inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-ink text-white text-sm font-semibold hover:bg-ink/90 transition-colors disabled:opacity-60"
            >
              <Save size={13} className="text-gold-500" />
              {saving ? "Saving…" : `Save ${dirtyCount}`}
            </button>
          </div>
        </div>
      )}

      <SalesVersionsDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onChanged={load}
      />
      <SalesSubmitModal
        open={submitOpen}
        onClose={() => setSubmitOpen(false)}
        onDone={() => { setSubmitOpen(false); load(); }}
      />
    </PageShell>
  );
}
